/**
 * VOICE-001 — how loud the microphone is right now, as a number from 0 to 1,
 * for the orb on the live voice screen to swell with.
 *
 * It listens to the stream the recorder is already writing from (see
 * `stream()` in voiceRecorder.ts) through a Web Audio analyser. Nothing is
 * connected to the speakers: the analyser is a tap, not a path, so the person
 * never hears themselves back.
 *
 * Pulled, not pushed. The orb reads `level()` once a frame from its own
 * animation loop; a meter that called back sixty times a second would mean a
 * React render sixty times a second for a value only the canvas cares about.
 *
 * Any failure — no AudioContext in this WebView, a context the browser refuses
 * to start — gives a meter that always reads 0. The orb then breathes instead
 * of following the voice, which is a smaller picture, not a broken screen.
 */
export interface LevelMeter {
  level(): number;
  close(): void;
}

const SILENT: LevelMeter = { level: () => 0, close: () => undefined };

type AudioContextCtor = new () => AudioContext;

export function createLevelMeter(stream: MediaStream): LevelMeter {
  const Ctor: AudioContextCtor | undefined =
    globalThis.AudioContext ?? (globalThis as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) return SILENT;
  let context: AudioContext | undefined;
  try {
    context = new Ctor();
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    // A context made outside a click can start suspended; the button that
    // opened the screen was the click, but a WebView may not count it.
    const ctx = context;
    void ctx.resume().catch(() => undefined);
    return {
      level: () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const value of samples) {
          const d = (value - 128) / 128;
          sum += d * d;
        }
        // RMS of speech at a normal distance sits around 0.05–0.15; the factor
        // puts ordinary talking in the upper half of the range, and shouting
        // at the cap rather than off it.
        return Math.min(1, Math.sqrt(sum / samples.length) * 6);
      },
      close: () => {
        source.disconnect();
        void ctx.close().catch(() => undefined);
      },
    };
  } catch {
    // A context made before the failure would otherwise stay open for good.
    void context?.close().catch(() => {});
    return SILENT;
  }
}
